import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import { 
    CalendarIcon, FileText, TrendingUp, ShoppingCart, Package, 
    AlertTriangle, Clock, XCircle, FileDown, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';

// Stat Card Component for consistency
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'accent', alert = false }) => {
    const colorClasses = {
        accent: { bg: 'bg-accent/10', text: 'text-accent', icon: 'text-accent' },
        green: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600', icon: 'text-green-600' },
        red: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600', icon: 'text-red-600' },
        orange: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-600', icon: 'text-orange-600' },
        terracotta: { bg: 'bg-terracotta/10', text: 'text-terracotta', icon: 'text-terracotta' },
        muted: { bg: 'bg-muted', text: 'text-foreground', icon: 'text-muted-foreground' }
    };
    
    const c = alert ? colorClasses[color] : (value === 0 ? colorClasses.muted : colorClasses[color]);
    
    return (
        <Card className="card-hover">
            <CardContent className="p-4 sm:p-6">
                <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">{title}</p>
                        <p className={`text-xl sm:text-2xl font-display font-bold mt-1 ${alert ? c.text : ''}`}>
                            {value}
                        </p>
                        {subtitle && (
                            <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
                        )}
                    </div>
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0 ml-2`}>
                        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${c.icon}`} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const ReportsPage = () => {
    const { api } = useAuth();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(new Date());
    const [calendarOpen, setCalendarOpen] = useState(false);

    useEffect(() => {
        fetchReport();
    }, [date]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            const response = await api().get(`/reports/daily?date=${dateStr}`);
            setReport(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement du rapport');
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = async () => {
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            const response = await api().get(`/export/report/excel?date=${dateStr}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `rapport_${dateStr}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Export Excel téléchargé');
        } catch (error) {
            toast.error('Erreur lors de l\'export');
        }
    };

    const handleExportPDF = async () => {
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            const response = await api().get(`/export/report/pdf?date=${dateStr}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `rapport_${dateStr}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Export PDF téléchargé');
        } catch (error) {
            toast.error('Erreur lors de l\'export');
        }
    };

    const goToPreviousDay = () => setDate(subDays(date, 1));
    const goToNextDay = () => {
        const tomorrow = addDays(date, 1);
        if (tomorrow <= new Date()) {
            setDate(tomorrow);
        }
    };
    const goToToday = () => setDate(new Date());

    const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

    return (
        <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6" data-testid="reports-page">
            {/* Header */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-foreground tracking-tight">
                            Rapport Journalier
                        </h1>
                        <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
                            Synthèse des activités
                        </p>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={fetchReport}
                        className="h-9"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
                
                {/* Date Navigation - Mobile Friendly */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                    {/* Date Selector */}
                    <div className="flex items-center gap-1 sm:gap-2">
                        <Button 
                            variant="outline" 
                            size="icon"
                            className="h-9 w-9"
                            onClick={goToPreviousDay}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        
                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                            <PopoverTrigger asChild>
                                <Button 
                                    variant="outline" 
                                    className="flex-1 sm:w-[200px] lg:w-[240px] justify-center sm:justify-start text-left font-normal h-9 text-xs sm:text-sm" 
                                    data-testid="date-picker-btn"
                                >
                                    <CalendarIcon className="mr-1.5 sm:mr-2 h-4 w-4 flex-shrink-0" />
                                    <span className="truncate">
                                        {format(date, 'EEE d MMM yyyy', { locale: fr })}
                                    </span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="center">
                                <Calendar
                                    mode="single"
                                    selected={date}
                                    onSelect={(newDate) => {
                                        if (newDate) {
                                            setDate(newDate);
                                            setCalendarOpen(false);
                                        }
                                    }}
                                    disabled={(d) => d > new Date()}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                        
                        <Button 
                            variant="outline" 
                            size="icon"
                            className="h-9 w-9"
                            onClick={goToNextDay}
                            disabled={isToday}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                        
                        {!isToday && (
                            <Button 
                                variant="secondary" 
                                size="sm"
                                className="h-9 text-xs hidden sm:flex"
                                onClick={goToToday}
                            >
                                Aujourd'hui
                            </Button>
                        )}
                    </div>
                    
                    {/* Export Buttons */}
                    <div className="flex gap-2">
                        {!isToday && (
                            <Button 
                                variant="secondary" 
                                size="sm"
                                className="h-9 text-xs sm:hidden flex-1"
                                onClick={goToToday}
                            >
                                Aujourd'hui
                            </Button>
                        )}
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleExportExcel} 
                            data-testid="export-report-excel-btn"
                            className="h-9 text-xs flex-1 sm:flex-none"
                        >
                            <FileDown className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">Excel</span>
                        </Button>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleExportPDF} 
                            data-testid="export-report-pdf-btn"
                            className="h-9 text-xs flex-1 sm:flex-none"
                        >
                            <FileDown className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">PDF</span>
                        </Button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-24 sm:h-32 bg-muted rounded-xl animate-pulse"></div>
                    ))}
                </div>
            ) : report ? (
                <>
                    {/* Stats Grid - 2 cols mobile, 3 cols desktop */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        <StatCard
                            title="Total Achats"
                            value={`${report.total_purchases.toLocaleString('fr-FR')} F`}
                            subtitle={`${report.total_purchases_count} achat(s)`}
                            icon={ShoppingCart}
                            color="accent"
                        />
                        
                        <StatCard
                            title="Entrées Stock"
                            value={`+${report.total_stock_entries}`}
                            subtitle="mouvements"
                            icon={TrendingUp}
                            color="green"
                        />
                        
                        <StatCard
                            title="Sorties Stock"
                            value={`-${report.total_stock_exits}`}
                            subtitle="mouvements"
                            icon={Package}
                            color="red"
                        />
                        
                        <StatCard
                            title="Stock Bas"
                            value={report.low_stock_products}
                            subtitle="à réapprovisionner"
                            icon={AlertTriangle}
                            color="terracotta"
                            alert={report.low_stock_products > 0}
                        />
                        
                        <StatCard
                            title="Péremption Proche"
                            value={report.expiring_soon_products}
                            subtitle="dans les 7 jours"
                            icon={Clock}
                            color="orange"
                            alert={report.expiring_soon_products > 0}
                        />
                        
                        <StatCard
                            title="Produits Périmés"
                            value={report.expired_products}
                            subtitle="à retirer"
                            icon={XCircle}
                            color="red"
                            alert={report.expired_products > 0}
                        />
                    </div>

                    {/* Total Stock Value - Full Width */}
                    <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
                        <CardContent className="p-4 sm:p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs sm:text-sm text-muted-foreground">Valeur Totale du Stock</p>
                                    <p className="text-2xl sm:text-3xl font-display font-bold text-accent mt-1">
                                        {report.total_stock_value.toLocaleString('fr-FR')} F
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">basée sur les prix d'achat</p>
                                </div>
                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Summary Card */}
                    <Card className="bg-muted/30">
                        <CardHeader className="pb-2 sm:pb-4">
                            <div className="flex items-center justify-between">
                                <CardTitle className="font-display text-base sm:text-lg">
                                    Résumé
                                </CardTitle>
                                <Badge variant="outline" className="text-xs">
                                    {format(date, 'EEE d MMM', { locale: fr })}
                                </Badge>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-background/50">
                                        <span className="text-muted-foreground text-xs sm:text-sm">Achats effectués</span>
                                        <span className="font-semibold">{report.total_purchases_count}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-background/50">
                                        <span className="text-muted-foreground text-xs sm:text-sm">Montant achats</span>
                                        <span className="font-semibold">{report.total_purchases.toLocaleString('fr-FR')} F</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-background/50">
                                        <span className="text-muted-foreground text-xs sm:text-sm">Stock bas</span>
                                        <span className={`font-semibold ${report.low_stock_products > 0 ? 'text-terracotta' : ''}`}>
                                            {report.low_stock_products}
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-background/50">
                                        <span className="text-muted-foreground text-xs sm:text-sm">Entrées</span>
                                        <span className="font-semibold text-green-600">+{report.total_stock_entries}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-background/50">
                                        <span className="text-muted-foreground text-xs sm:text-sm">Sorties</span>
                                        <span className="font-semibold text-red-600">-{report.total_stock_exits}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-background/50">
                                        <span className="text-muted-foreground text-xs sm:text-sm">Alertes péremption</span>
                                        <span className={`font-semibold ${(report.expiring_soon_products + report.expired_products) > 0 ? 'text-orange-600' : ''}`}>
                                            {report.expiring_soon_products + report.expired_products}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </>
            ) : (
                <Card className="p-8 sm:p-12">
                    <div className="text-center">
                        <FileText className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground text-sm sm:text-base">Aucune donnée pour cette date</p>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-4"
                            onClick={goToToday}
                        >
                            Voir aujourd'hui
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default ReportsPage;
